"use client"
import { useMediaQuery, useTheme } from "@mui/material"
import UseFocusFirstInvalidField from "common/UseFocusFirstInvalidField"
import { Form, Formik } from "formik"
import { useMemo, useRef, useState } from "react"
import CheckCircleIcon from "reusable/icons/CheckCircleIcon"
import ErrorCircleIcon from "reusable/icons/ErrorCircleIcon"
import styled from "styled-components"
import { trackNewsletterSignup } from "utils/gtm"
import * as yup from "yup"
import { NewsletterPlatform, submitNewsletter } from "./submitNewsletter"

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const schema = yup
  .object({
    email: yup
      .string()
      .required("כתובת המייל אינה תקנית")
      .matches(EMAIL_REGEX, "כתובת המייל אינה תקנית"),
  })
  .defined()

const initialValues: yup.InferType<typeof schema> = { email: "" }

type Status = "idle" | "submitting" | "success" | "error"

const StyledForm = styled(Form)<{ $invalid: boolean; $terminal: boolean }>`
  display: flex;
  width: 100%;
  position: relative;
  isolation: isolate;
  align-items: stretch;

  /* desktop: shared bottom border under button + field */
  ${(p) => p.theme.breakpoints.up("md")} {
    border-bottom: 2px solid
      ${(p) => (p.$invalid ? p.theme.colors.dangerRed : p.theme.colors.black)};
    ${(p) => (p.$terminal ? "border-bottom: none;" : "")}
  }

  /* mobile: stacked */
  ${(p) => p.theme.breakpoints.down("md")} {
    flex-direction: column;
    gap: 32px;
  }
`

const SubmitButton = styled.button`
  z-index: 2;
  background-color: ${(p) => p.theme.colors.black};
  border: 2px solid ${(p) => p.theme.colors.black};
  color: ${(p) => p.theme.colors.white};
  cursor: pointer;
  padding: 18px 26px;
  // Native <button> doesn't inherit font-family — set it explicitly to match
  // the section's Hebrew text (DLK-727).
  font-family: OENaPro, sans-serif;
  font-size: 16px;
  line-height: 18px;
  white-space: nowrap;

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }

  ${(p) => p.theme.breakpoints.down("md")} {
    width: 100%;
  }
`

const FieldArea = styled.div`
  z-index: 1;
  flex: 1 0 0;
  min-width: 0;
  align-self: stretch;
  position: relative;
  display: flex;
  align-items: flex-end;
`

const EmailInput = styled("input")<{ $invalid: boolean }>`
  width: 100%;
  border: none;
  background: transparent;
  text-align: right;
  // Native <input> doesn't inherit font-family; the placeholder follows it (DLK-727).
  font-family: OENaPro, sans-serif;
  font-size: 18px;
  line-height: 28px;
  color: ${(p) => p.theme.colors.black};
  padding: 12px;
  outline: none;

  &::placeholder {
    color: ${(p) => p.theme.colors.neutral};
  }

  /* mobile: rounded bordered box */
  ${(p) => p.theme.breakpoints.down("md")} {
    border: 0.5px solid
      ${(p) =>
        p.$invalid
          ? p.theme.colors.dangerRed
          : p.theme.colors.newsletterInputBorder};
    border-radius: 4px;
    background: ${(p) => p.theme.colors.white};
    height: 45px;
    padding: 12px;
  }
`

const FloatingLabel = styled.span`
  position: absolute;
  top: -18px;
  right: 0;
  font-size: 14px;
  line-height: 18px;
  color: ${(p) => p.theme.colors.steel};

  ${(p) => p.theme.breakpoints.down("md")} {
    top: -20px;
  }
`

const StatusRow = styled.div<{ $success: boolean }>`
  flex: 1 0 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 12px 0;
  font-weight: 700;
  font-size: 20px;
  line-height: 18px;
  color: ${(p) =>
    p.$success ? p.theme.colors.successGreen : p.theme.colors.dangerRed};

  ${(p) => p.theme.breakpoints.down("md")} {
    width: 100%;
  }
`

const ValidationMessage = styled.span`
  display: block;
  width: 100%;
  text-align: right;
  margin-top: 4px;
  font-size: 14px;
  line-height: 18px;
  color: ${(p) => p.theme.colors.dangerRed};
  position: absolute;
  bottom: -20px;
  right: 0;

  ${(p) => p.theme.breakpoints.down("md")} {
    bottom: -24px;
    font-size: 12px;
    line-height: 16px;
  }
`

const NewsletterForm = () => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))

  const [status, setStatus] = useState<Status>("idle")

  // Deliberately two-way (Pc/Mobile) — tablets report as "Mobile". The API's
  // ContactSource also allows "Tablet", but product doesn't segment it here.
  const platform: NewsletterPlatform = isDesktop ? "Pc" : "Mobile"
  const isTerminal = status === "success" || status === "error"

  // Single persistently-mounted live region. aria-live only announces content
  // changes within an already-observed node, so the visual StatusRow/validation
  // message (which mount/unmount) can't carry the announcement themselves.
  const liveMessage =
    status === "success"
      ? "נשלח בהצלחה!"
      : status === "error"
      ? "פנייתך לא נשלחה"
      : ""

  const emailRef = useRef<HTMLInputElement>(null)

  const fieldNameInputRefMap = useMemo(
    () => ({
      email: emailRef,
    }),
    []
  )

  const handleSubmit = async ({ email }: typeof initialValues) => {
    if (status === "submitting") return
    setStatus("submitting")
    try {
      const ok = await submitNewsletter(email, platform)
      if (ok) trackNewsletterSignup()
      setStatus(ok ? "success" : "error")
    } catch {
      setStatus("error")
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {(formik) => {
        const invalid = !!(
          formik.submitCount > 0 &&
          formik.errors.email &&
          formik.touched.email
        )
        return (
          <StyledForm $invalid={invalid} $terminal={isTerminal} noValidate>
            {isTerminal ? (
              <StatusRow $success={status === "success"}>
                {status === "success" ? (
                  <CheckCircleIcon />
                ) : (
                  <ErrorCircleIcon />
                )}
                {liveMessage}
              </StatusRow>
            ) : (
              <FieldArea>
                {formik.values.email && (
                  <FloatingLabel>כתובת המייל שלכם</FloatingLabel>
                )}
                <UseFocusFirstInvalidField
                  fieldNameInputRefMap={fieldNameInputRefMap}
                />
                <EmailInput
                  dir="auto"
                  $invalid={invalid}
                  placeholder="כתובת המייל שלכם"
                  aria-label="כתובת המייל שלכם"
                  aria-invalid={invalid}
                  aria-describedby={
                    invalid ? "newsletter-email-error" : undefined
                  }
                  ref={emailRef}
                  required
                  name="email"
                  id="email"
                  autoComplete="email"
                  onChange={(e) => {
                    formik.handleChange(e)
                    formik.setFieldTouched("email", true, false)
                  }}
                />
                {invalid && (
                  <ValidationMessage id="newsletter-email-error">
                    {formik.errors.email}
                  </ValidationMessage>
                )}
              </FieldArea>
            )}

            <SubmitButton
              type={isTerminal ? "button" : "submit"}
              disabled={status === "submitting"}
              aria-busy={status === "submitting"}
              onClick={() => {
                if (isTerminal) {
                  setStatus("idle")
                  formik.resetForm()
                }
              }}
            >
              הרשמה
            </SubmitButton>
          </StyledForm>
        )
      }}
    </Formik>
  )
}

export default NewsletterForm
